// Private student/professor photos: credentials travel only in headers, never image URLs.
const StudentPhotos = {
    _identityMarkup: new WeakMap(),
    setIdentityHTML(element, html) {
        // Badge polling must not replace a loaded portrait with a fresh, empty img.
        // Only retain the live DOM; never cache private images in browser storage.
        const userId = AuthManager.getCurrentUser()?.id;
        const epoch = AuthManager._epoch;
        const previous = this._identityMarkup.get(element);
        if (previous && previous.html === html && previous.userId === userId &&
            previous.epoch === epoch && previous.child === element.firstChild) return;
        SafeHTML.set(element, html);
        this._identityMarkup.set(element, { html, userId, epoch, child: element.firstChild });
    },
    attributes(value) {
        const escape = text => String(text).replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
        try {
            if (typeof value !== 'string' || !value) return '';
            const url = new URL(value, window.location.origin + '/');
            if (url.origin === window.location.origin && url.pathname.startsWith('/uploads/')) {
                return `data-student-photo="${escape(this.resolve(value))}"`;
            }
            // Preserve existing embedded raster portraits and ordinary public image assets.
            if (/^data:image\/(?:png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+$/i.test(value) ||
                (url.origin === window.location.origin && url.pathname.startsWith('/assets/')) ||
                (url.protocol === 'https:' && url.origin !== window.location.origin)) {
                return `src="${escape(value)}" referrerpolicy="no-referrer"`;
            }
        } catch { /* Unsafe/unknown values leave the placeholder empty. */ }
        return '';
    },
    resolve(value) {
        const url = new URL(value, window.location.origin + '/');
        if (url.origin !== window.location.origin || !/^\/uploads\/(?:alumnos|temp|avatars)\/[^/]+$/.test(url.pathname)) throw new Error('Foto no permitida');
        url.search = ''; url.hash = '';
        return url.href;
    },
    // Photos already downloaded in this session, kept in memory only (never in browser storage)
    // so that re-drawing a list (sorting, coming back to it) does not fetch every photo again.
    // Forgotten when the session changes; the oldest are dropped past MAX_CACHED.
    MAX_CACHED: 300,
    _cache: new Map(),
    _cacheEpoch: null,
    forget() {
        for (const entry of this._cache.values()) entry.then(blobUrl => URL.revokeObjectURL(blobUrl), () => {});
        this._cache.clear();
    },
    /** @returns {Promise<string>} An object URL for the photo, downloaded once per session. */
    cached(url, token) {
        if (this._cacheEpoch !== AuthManager._epoch) { this.forget(); this._cacheEpoch = AuthManager._epoch; }
        if (!this._cache.has(url)) {
            const entry = (async () => {
                const response = await fetch(url, {headers:{'X-SICAF-CSRF': token},cache:'no-store',redirect:'error'});
                if (!response.ok || !/^image\/(png|jpeg|gif|webp)(?:;|$)/i.test(response.headers.get('content-type') || '')) throw new Error('Foto no disponible');
                const blob = await response.blob();
                if (blob.size > 10 * 1024 * 1024) throw new Error('Foto no disponible');
                return URL.createObjectURL(blob);
            })();
            entry.catch(() => this._cache.delete(url)); // A failure is retried next time.
            this._cache.set(url, entry);
            if (this._cache.size > this.MAX_CACHED) {
                const [oldest, old] = this._cache.entries().next().value;
                this._cache.delete(oldest);
                old.then(blobUrl => URL.revokeObjectURL(blobUrl), () => {});
            }
        }
        return this._cache.get(url);
    },
    async load(image) {
        const value = image.dataset.studentPhoto;
        if (image.dataset.photoLoading) return;
        image.dataset.photoLoading = '1';
        try {
            const url = this.resolve(value), token = AuthManager.getCsrfToken();
            if (!token) throw new Error('Sin sesión');
            const blobUrl = await this.cached(url, token);
            if (!image.isConnected || AuthManager.getCsrfToken() !== token) return;
            image.src = blobUrl;
        } catch {
            image.alt = 'Foto no disponible';
        }
    },
    scan(root) {
        if (root.matches?.('img[data-student-photo]')) this.load(root);
        root.querySelectorAll?.('img[data-student-photo]').forEach(image => this.load(image));
    }
};
new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => StudentPhotos.scan(node))))
    .observe(document.documentElement, {childList:true,subtree:true});
StudentPhotos.scan(document);
window.StudentPhotos = StudentPhotos;
